/**
 * Cleans up the input string by removing all non-alphanumeric characters
 * except spaces, and trimming leading and trailing whitespace.
 *
 * This function is useful for sanitizing user input, ensuring that only
 * letters, numbers, and spaces remain in the string.
 *
 * @param {string} str - The input string to be sanitized.
 * @returns {string} The cleaned input string with non-alphanumeric characters (except spaces) removed.
 */
export function cleanInput(str: string): string {
  return str.trim().replace(/[^a-zA-Z0-9 ]/g, "");
}

export function validateNameInput(str: string) {
  const trimmedInput = str.trim();
  const regex = /^[A-Za-z\s]+$/;
  return regex.test(trimmedInput);
}


/**
 * Highlights matching keywords in a given text by wrapping them in a `<span>` with a bold class.
 *
 * @param {string} text - The input text where the keyword will be highlighted.
 * @param {string} keyword - The keyword to search for and highlight in the text.
 * @returns {string} - The modified text with the keyword wrapped in a `<span class="font-bold">` tag.
 *
 * @example
 * const text = "This is a sentence with a highlighted keyword.";
 * const keyword = "keyword";
 * const result = boldMatchingText(text, keyword);
 * console.log(result); // Output: "This is a sentence with a highlighted <span class="font-bold">**keyword**</span>."
 */

export function boldMatchingText(text: string, keyword: string): string {
  if (!text) return "";
  if (!keyword) return text;

  const lowerText = text.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();

  let index = lowerText.indexOf(lowerKeyword);
  if (index === -1) return text;

  const before = text.substring(0, index)
  const match = text.substring(index, index + keyword.length);
  const after = text.substring(index + keyword.length);

  return `${before}<span class="font-bold">${match}</span>${after}`;
}


/**
 * Capitalizes each word in a given string
 *
 * @param {string | undefined} str The input string to capitalize. If `undefined`, an empty string is returned.
 * @returns {string} The capitalized string, with the first letter of each word in uppercase and the rest in lowercase.
 *
 * @example
 * const result1 = useCapitalize("hello world");
 * console.log(result1); // Output: "Hello World"
 */
export function useCapitalize(str?: string): string {
  if (!str) return "";

  return str
    .toLowerCase()
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};


/**
 * Splits a `camelCase` string into separate words by inserting spaces between `camel Case` segments.
 *
 * @param {string} input - The camelCase string to split.
 * @returns {string} - The input string with spaces inserted between camelCase segments.
 *
 * @example
 * const result1 = splitCamelCase("camelCaseString");
 * console.log(result1); // Output: "camel Case String"
 */
export function splitCamelCase(input: string): string {
  // Regular expression to match the transition from lowercase to uppercase letters
  const regex = /([a-z])([A-Z])/g;

  // Insert a space between the matched lowercase and uppercase letters
  const result = input.replace(regex, '$1 $2');

  return result;
}


export function encryptToken(token: string) {
  return btoa(
      String.fromCharCode(...new Uint8Array(new TextEncoder().encode(token)))
  );
}


export function decryptToken(encryptedToken: string) {
  return new TextDecoder().decode(
      new Uint8Array([...atob(encryptedToken)].map((c) => c.charCodeAt(0)))
  );
}

export function toCamelCase (str: string) {
  return str.replace(/ (\w)/g, (_, c) => c.toUpperCase());
}

export async function generateKeyFromObject(obj: Record<string, any>) {
  const json = JSON.stringify(obj)
  const encoder = new TextEncoder();
  const data = encoder.encode(json);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}